import { FC, useEffect, useState, MouseEvent } from "react";
import { styled } from "@mui/material/styles";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import { Colors } from "../../../../configs";
import { ReleaseDrawer } from "../../../../components/admin/release-drawer/ReleaseDrawer";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { getAllReleasesAction, ReleaseDto } from "../../../../redux/actions/customerReleaseSignatureAction";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: { fontWeight: "bold" },
  [`&.${tableCellClasses.body}`]: { fontSize: "var(--fs-base)" },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
}));

export const ReleasesPage: FC = () => {
  const dispatch = useAppDispatch();
  const { releases, getAllLoading, createLoading, updateLoading } = useAppSelector(
    (s) => s.release
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRelease, setSelectedRelease] = useState<ReleaseDto | undefined>(undefined);
  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    dispatch(getAllReleasesAction());
  }, [dispatch]);

  const handleMenuOpen = (e: MouseEvent<HTMLButtonElement>, release: ReleaseDto) => {
    setAnchorEl(e.currentTarget);
    setSelectedRelease(release);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const isLoading = getAllLoading || createLoading || updateLoading;

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "3rem",
          height: "57.5rem",
          marginTop: "4.357rem",
          paddingInline: "5rem",
          width: "100%",
        }}
      >
        <ReleaseDrawer
          open={openDrawer}
          toggleDrawer={() => setOpenDrawer(false)}
          populateFor={selectedRelease}
        />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
            Releases
          </Typography>
          <CustomButton
            sx={{ width: "12rem", height: "3rem" }}
            onClick={() => {
              setSelectedRelease(undefined);
              setOpenDrawer(true);
            }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={27} color="inherit" /> : "Add Release"}
          </CustomButton>
        </Box>

        <Box
          sx={{
            maxHeight: "51rem",
            width: "100%",
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "0.5rem" },
            "&::-webkit-scrollbar-thumb": { background: "#888" },
          }}
        >
          <Table sx={{ width: "100%" }} aria-label="releases table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Agreement Text</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getAllLoading && (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} align="center">
                    <CircularProgress size={27} color="inherit" />
                  </StyledTableCell>
                </StyledTableRow>
              )}
              {!getAllLoading &&
                releases.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell sx={{ width: "18%", fontFamily: "monospace", fontWeight: 600 }}>
                      {row.name}
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: "22%" }}>
                      {row.description ?? "—"}
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: "42%", color: "#666" }}>
                      {row.agreementText
                        ? row.agreementText.length > 120
                          ? `${row.agreementText.slice(0, 120)}…`
                          : row.agreementText
                        : <span style={{ color: "#bbb" }}>No agreement text</span>}
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: "10%" }}>
                      <Chip
                        label={row.active ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          backgroundColor: row.active ? "#e6f4ef" : "#f0f0f0",
                          color: row.active ? "#018752" : "#888",
                          fontWeight: 600,
                          fontSize: "var(--fs-2xs)",
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: "8%" }}>
                      <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreHorizIcon sx={{ fontSize: "2rem" }} color="primary" />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              {!isLoading && releases.length === 0 && (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} align="center" sx={{ py: "3rem", color: "#aaa" }}>
                    No releases yet. Click "Add Release" to create one.
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem
              onClick={() => {
                setOpenDrawer(true);
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Release</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </AdminLayout>
  );
};
